import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import {
  CardComp,
  ButtonComp,
  ModalCompAdvance,
  ModalCompAdvanceUpdate,
} from "../../../../components";
import CustomSelect from "../../../../components/custom-select/custom-select";
import { customStyles } from "../../../../components/custom-select/custom-styles";
import { useGetBanksQuery } from "../../../../store/selectableSlice";
import {
  useGetMemberBankAccountQuery as useGetAdminMemberBankAccountQuery,
  useAddMemberBankAccountMutation as useAddAdminMemberBankAccountMutation,
  useEditMemberBankAccountMutation as useEditAdminMemberBankAccountMutation,
  useValidateBankAccountMutation as useValidateAdminBankAccountMutation,
  useDeleteMemberBankAccountMutation,
} from "../../../../store/admin/memberBankAccountSlice";
import { Input } from "../../../../components/Ui/new-input";
import Loader from "../../../../components/Ui/Loader";
import ConfirmModal from "../ConfirmModal";

const BankAccountTab = ({ memberId }) => {
  // This component is ONLY for admin/staff viewing member profiles
  // Members viewing their own profile use MemberBankAccountTab component
  // memberId is REQUIRED - this is the member whose bank account we're managing

  const [modalPage, setModalPage] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [validatedAccountName, setValidatedAccountName] = useState("");
  const [validationError, setValidationError] = useState("");

  // Admin endpoints only - memberId is required
  const {
    data: bankAccountAdmin,
    isLoading: isLoadingAdmin,
    refetch: refetchAdmin,
    error: errorAdmin,
  } = useGetAdminMemberBankAccountQuery(memberId, {
    skip: !memberId, // Skip if no memberId
  });

  // Determine if we have a valid bank account
  // Check if data exists and has required fields (id or account_number)
  const hasValidBankAccount = (account) => {
    if (!account) return false;
    if (typeof account !== "object") return false;
    return !!(account.id || account.account_number);
  };

  // Determine final bank account value (admin context only)
  const bankAccount = hasValidBankAccount(bankAccountAdmin)
    ? bankAccountAdmin
    : null;
  const isLoading = isLoadingAdmin;
  const refetch = refetchAdmin;

  const { data: banks = [], isLoading: isLoadingBanks } = useGetBanksQuery();

  const [addAdminBankAccount, { isLoading: isAddingAdmin }] =
    useAddAdminMemberBankAccountMutation();
  const [editAdminBankAccount, { isLoading: isEditingAdmin }] =
    useEditAdminMemberBankAccountMutation();
  const [validateAdminAccount, { isLoading: isValidatingAdmin }] =
    useValidateAdminBankAccountMutation();
  const [deleteBankAccount, { isLoading: isDeleting }] =
    useDeleteMemberBankAccountMutation();

  const isAdding = isAddingAdmin;
  const isEditing = isEditingAdmin;
  const isValidating = isValidatingAdmin;

  const formik = useFormik({
    initialValues: {
      bank_id: "",
      account_number: "",
      account_name: "",
    },
    validationSchema: Yup.object({
      bank_id: Yup.string().required("Bank is required"),
      account_number: Yup.string()
        .required("Account number is required")
        .length(10, "Account number must be exactly 10 digits")
        .matches(/^\d+$/, "Account number must contain only digits"),
      account_name: Yup.string().required("Account name is required"),
    }),
    onSubmit: async (values) => {
      try {
        const payload = {
          memberId,
          bank_id: parseInt(values.bank_id),
          account_number: values.account_number,
          account_name: values.account_name,
        };

        // Admin can add or edit
        if (isEditMode && bankAccount?.id) {
          await editAdminBankAccount({
            memberId,
            bankAccountId: bankAccount.id,
            bank_id: parseInt(values.bank_id),
            account_number: values.account_number,
            account_name: values.account_name,
          }).unwrap();
          toast.success("Bank account updated successfully");
        } else {
          await addAdminBankAccount(payload).unwrap();
          toast.success("Bank account added successfully");
        }

        setModalPage("");
        setIsEditMode(false);
        formik.resetForm();
        setValidatedAccountName("");
        setValidationError("");
        refetch();
      } catch (error) {
        toast.error(error?.data?.message || "Failed to save bank account");
      }
    },
  });

  useEffect(() => {
    if (bankAccount && modalPage === "Edit Bank Account") {
      setIsEditMode(true);
      const selectedBank = banks.find(
        (bank) => bank.code === bankAccount.bank_code
      );
      formik.setValues({
        bank_id: selectedBank?.id?.toString() || "",
        account_number: bankAccount.account_number || "",
        account_name: bankAccount.account_name || "",
      });
      setValidatedAccountName(bankAccount.account_name || "");
    } else if (!modalPage) {
      setIsEditMode(false);
      formik.resetForm();
      setValidatedAccountName("");
      setValidationError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankAccount, modalPage, banks]);

  const handleValidateAccount = React.useCallback(async () => {
    if (!formik.values.bank_id || !formik.values.account_number) {
      return;
    }

    if (formik.values.account_number.length !== 10) {
      return;
    }

    try {
      const selectedBank = banks.find(
        (bank) => bank.id === parseInt(formik.values.bank_id)
      );
      if (!selectedBank) {
        toast.error("Please select a bank");
        return;
      }

      const response = await validateAdminAccount({
        memberId,
        account_number: formik.values.account_number,
        bank_code: selectedBank.code,
      }).unwrap();

      // Handle response - check for account_name in response
      // Response structure after transformResponse: { account_number, account_name, bank_id }
      // The transformResponse extracts responseData.data, so response should be the data object directly
      // Also handle case where response might be the full object with status/message/data
      const accountName =
        response?.account_name || response?.data?.account_name;

      // Debug logging to help identify response structure issues
      if (process.env.NODE_ENV === "development") {
        console.log("Validation response:", response);
        console.log("Extracted account_name:", accountName);
        console.log(
          "Response keys:",
          response ? Object.keys(response) : "null"
        );
      }

      // Check if account_name exists and is a non-empty string
      // Also check if response itself is valid (not null/undefined)
      if (
        response &&
        accountName &&
        typeof accountName === "string" &&
        accountName.trim().length > 0
      ) {
        setValidatedAccountName(accountName);
        formik.setFieldValue("account_name", accountName);
        setValidationError("");
        toast.success("Account validated successfully");
      } else {
        // Log the response for debugging
        console.warn("Validation response structure:", response);
        console.warn(
          "Account name check failed. accountName:",
          accountName,
          "type:",
          typeof accountName
        );
        console.warn(
          "Response exists:",
          !!response,
          "Response type:",
          typeof response
        );
        setValidatedAccountName("");
        formik.setFieldValue("account_name", "");
        setValidationError(
          "Could not validate account. Please check the account number and bank."
        );
      }
    } catch (error) {
      setValidatedAccountName("");
      formik.setFieldValue("account_name", "");
      const errorMessage =
        error?.data?.message ||
        "Failed to validate account. Please check the account number and bank.";
      setValidationError(errorMessage);
      toast.error(errorMessage);
    }
  }, [
    formik.values.bank_id,
    formik.values.account_number,
    banks,
    memberId,
    validateAdminAccount,
  ]);

  useEffect(() => {
    if (formik.values.account_number?.length === 10 && formik.values.bank_id) {
      handleValidateAccount();
    } else {
      setValidationError("");
      setValidatedAccountName("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values.account_number,
    formik.values.bank_id,
    handleValidateAccount,
  ]);

  const handleDeleteBankAccount = async () => {
    if (!bankAccount?.id) return;

    try {
      await deleteBankAccount({
        memberId,
        bankAccountId: bankAccount.id,
      }).unwrap();
      toast.success("Bank account deleted successfully");
      setModalPage("");
      refetch();
    } catch (error) {
      toast.error(error?.data?.message || "Failed to delete bank account");
    }
  };

  const useModal = [
    {
      name: "Delete Bank Account",
      component: (
        <ConfirmModal
          title="Confirm Delete"
          desc="Are you sure you want to delete this bank account? This action cannot be undone."
          setModalToggle={() => setModalPage("")}
          confimAction={handleDeleteBankAccount}
          isLoading={isDeleting}
        />
      ),
    },
    {
      name: "Add Bank Account",
      component: (
        <div>
          <h4 className="mb-0 text-xl py-3 text-black1 fw-bold text-center">
            Add Bank Account
          </h4>
          <p className="text-sm text-gray-600 mb-4 text-center">
            Enter the member's bank account details. We'll verify the account
            number automatically.
          </p>
          <form className="w-full" onSubmit={formik.handleSubmit}>
            <div className="space-y-4">
              <div>
                <CustomSelect
                  options={banks.map((bank) => ({
                    value: bank.id.toString(),
                    label: bank.title || bank.name,
                  }))}
                  value={formik.values.bank_id || null}
                  styles={customStyles}
                  label="Bank"
                  isLoading={isLoadingBanks}
                  onChange={(value) => {
                    formik.setFieldValue("bank_id", value.value);
                    setValidatedAccountName("");
                    setValidationError("");
                  }}
                  placeholder="Select bank"
                />
                {formik.touched.bank_id && formik.errors.bank_id && (
                  <span className="text-sm text-red-500 font-circular_book block w-full mt-1">
                    {formik.errors.bank_id}
                  </span>
                )}
              </div>

              <div>
                <Input
                  type="text"
                  label="Account Number"
                  placeholder="Enter 10-digit account number"
                  name="account_number"
                  onChange={(e) => {
                    formik.handleChange(e);
                    setValidationError("");
                    setValidatedAccountName("");
                  }}
                  onBlur={formik.handleBlur}
                  value={formik.values.account_number}
                  error={
                    formik.touched.account_number &&
                    formik.errors.account_number
                  }
                  errorMessage={formik.errors.account_number}
                  maxLength={10}
                />
              </div>

              <div>
                <Input
                  label="Account Name"
                  placeholder={
                    isValidating
                      ? "Validating account..."
                      : "Account name will appear after validation"
                  }
                  name="account_name"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.account_name}
                  error={
                    formik.touched.account_name && formik.errors.account_name
                  }
                  errorMessage={formik.errors.account_name}
                  readOnly
                  loading={isValidating}
                />
              </div>

              {validatedAccountName && (
                <div className="p-3 bg-green-50 border border-green-200 rounded">
                  <p className="text-sm text-green-700 mb-0">
                    ✓ Account validated: <strong>{validatedAccountName}</strong>
                  </p>
                </div>
              )}

              {validationError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded">
                  <p className="text-sm text-red-700 mb-0">{validationError}</p>
                </div>
              )}
            </div>

            <div className="my-4 w-full flex justify-center">
              <button
                type="submit"
                className="w-[200px] h-[50px] rounded-[10px] bg-new-primary text-white disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!validatedAccountName || isValidating || isAdding}
              >
                {isAdding ? "Saving..." : "Save Bank Account"}
              </button>
            </div>
          </form>
        </div>
      ),
    },
    {
      name: "Edit Bank Account",
      component: (
        <div>
          <h4 className="mb-0 text-xl py-3 text-black1 fw-bold text-center">
            Edit Bank Account
          </h4>
          <p className="text-sm text-gray-600 mb-4 text-center">
            Update the member's bank account details. We'll verify the account
            number automatically.
          </p>
          <form className="w-full" onSubmit={formik.handleSubmit}>
            <div className="space-y-4">
              <div>
                <CustomSelect
                  options={banks.map((bank) => ({
                    value: bank.id.toString(),
                    label: bank.title || bank.name,
                  }))}
                  value={formik.values.bank_id || null}
                  styles={customStyles}
                  label="Bank"
                  isLoading={isLoadingBanks}
                  onChange={(value) => {
                    formik.setFieldValue("bank_id", value.value);
                    setValidatedAccountName("");
                    setValidationError("");
                  }}
                  placeholder="Select bank"
                />
                {formik.touched.bank_id && formik.errors.bank_id && (
                  <span className="text-sm text-red-500 font-circular_book block w-full mt-1">
                    {formik.errors.bank_id}
                  </span>
                )}
              </div>

              <div>
                <Input
                  type="text"
                  label="Account Number"
                  placeholder="Enter 10-digit account number"
                  name="account_number"
                  onChange={(e) => {
                    formik.handleChange(e);
                    setValidationError("");
                    setValidatedAccountName("");
                  }}
                  onBlur={formik.handleBlur}
                  value={formik.values.account_number}
                  error={
                    formik.touched.account_number &&
                    formik.errors.account_number
                  }
                  errorMessage={formik.errors.account_number}
                  maxLength={10}
                />
              </div>

              <div>
                <Input
                  label="Account Name"
                  placeholder={
                    isValidating
                      ? "Validating account..."
                      : "Account name will appear after validation"
                  }
                  name="account_name"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.account_name}
                  error={
                    formik.touched.account_name && formik.errors.account_name
                  }
                  errorMessage={formik.errors.account_name}
                  readOnly
                  loading={isValidating}
                />
              </div>

              {validatedAccountName && (
                <div className="p-3 bg-green-50 border border-green-200 rounded">
                  <p className="text-sm text-green-700 mb-0">
                    ✓ Account validated: <strong>{validatedAccountName}</strong>
                  </p>
                </div>
              )}

              {validationError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded">
                  <p className="text-sm text-red-700 mb-0">{validationError}</p>
                </div>
              )}
            </div>

            <div className="my-4 w-full flex justify-center">
              <button
                type="submit"
                className="w-[200px] h-[50px] rounded-[10px] bg-new-primary text-white disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!validatedAccountName || isValidating || isEditing}
              >
                {isEditing ? "Updating..." : "Update Bank Account"}
              </button>
            </div>
          </form>
        </div>
      ),
    },
  ];

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-4">
        <h5 className="fw-bold text-dark mb-0">Bank Account Information</h5>
        {!isLoading && (
          <div className="flex gap-2">
            {bankAccount ? (
              <>
                <ButtonComp
                  btnText="Edit Bank Account"
                  onClick={() => setModalPage("Edit Bank Account")}
                  btnClassName="bg-primary1 text-white border-0 rounded px-4 py-2 fw-semibold hover:opacity-90 transition-opacity"
                  ButtonInlineStyle={{ minWidth: "fit-content" }}
                />
                <ButtonComp
                  btnText="Delete"
                  onClick={() => setModalPage("Delete Bank Account")}
                  btnClassName="bg-red-600 text-white border-0 rounded px-4 py-2 fw-semibold hover:opacity-90 transition-opacity"
                  ButtonInlineStyle={{ minWidth: "fit-content" }}
                />
              </>
            ) : (
              <ButtonComp
                btnText="Add Bank Account"
                onClick={() => setModalPage("Add Bank Account")}
                btnClassName="bg-primary1 text-white border-0 rounded px-4 py-2 fw-semibold hover:opacity-90 transition-opacity"
                ButtonInlineStyle={{ minWidth: "fit-content" }}
              />
            )}
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-10">
          <Loader />
        </div>
      ) : bankAccount ? (
        <CardComp cardClassName="border-0 shadow-sm">
          <div className="p-4 space-y-4">
            <div>
              <p className="text-muted text-sm mb-1">Bank Name</p>
              <p className="fw-semibold text-dark">
                {bankAccount.bank_name || "N/A"}
              </p>
            </div>
            <div>
              <p className="text-muted text-sm mb-1">Account Number</p>
              <p className="fw-semibold text-dark">
                {bankAccount.account_number || "N/A"}
              </p>
            </div>
            <div>
              <p className="text-muted text-sm mb-1">Account Name</p>
              <p className="fw-semibold text-dark">
                {bankAccount.account_name || "N/A"}
              </p>
            </div>
          </div>
        </CardComp>
      ) : !isLoading ? (
        <CardComp cardClassName="border-0 shadow-sm">
          <div className="p-4 text-center">
            <p className="text-muted mb-0">
              No bank account information available
            </p>
          </div>
        </CardComp>
      ) : null}

      {/* All Modals */}
      <ModalCompAdvanceUpdate
        pageName={modalPage}
        arrayComp={useModal}
        handleClose={() => {
          setModalPage("");
          setIsEditMode(false);
          formik.resetForm();
          setValidatedAccountName("");
          setValidationError("");
        }}
        centered
        size="md"
      />
    </div>
  );
};

export default BankAccountTab;
